import React from "react";
import Select, {
  OptionProps,
  SingleValue,
  SingleValueProps,
  StylesConfig,
  components,
} from "react-select";
import InfoTooltip from "../common/InfoTooltip";

export interface OptionType {
  label: string;
  value: any;
  description: string;
  icon?: any;
}

type SelectProps = {
  label: string;
  placeholder: string;
  options: OptionType[];
  selected?: SingleValue<OptionType>;
  setSelected: (option: SingleValue<OptionType>) => void;
  tooltip?: string;
  className?: string;
  isClearable?: boolean;
  maxMenuHeight?: number;
  disabled?: boolean;
  showDescriptionBelowLabel?: boolean;
};

const CustomOption =
  (showDescriptionBelowLabel: boolean) => (props: OptionProps<OptionType>) => {
    return (
      <components.Option {...props}>
        <div className="flex items-start px-2">
          {props?.data?.icon && (
            <img
              alt="select-option-icon"
              src={props.data.icon}
              className="w-5 h-5 mr-2"
            />
          )}
          <div className="flex flex-col min-w-[8rem] md:min-w-[12rem]">
            <span className="text-sm">{props.data.label}</span>
            {showDescriptionBelowLabel && props.data.description && (
              <span className="text-xs text-gray-500 leading-4 mt-0.5">
                {props.data.description}
              </span>
            )}
          </div>
          {!showDescriptionBelowLabel && props.data.description && (
            <div className="text-sm text-gray-500">
              {props.data.description}
            </div>
          )}
        </div>
      </components.Option>
    );
  };

const SingleValueComponent = (props: SingleValueProps<OptionType>) => {
  return (
    <components.SingleValue {...props}>
      <div className="flex items-start md:items-center px-0">
        {props?.data?.icon && (
          <img
            alt="select-option-icon"
            src={props.data.icon}
            className="w-5 h-5 mr-2"
          />
        )}
        <div className="text-sm min-w-[8rem] md:min-w-[12rem]">
          {props.data.label}
        </div>
      </div>
    </components.SingleValue>
  );
};

const customStyles: StylesConfig<OptionType, false> = {
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#e5e7eb"
      : state.isFocused
        ? "#f3f4f6"
        : "#fff",
    color: state.isSelected ? "#1f2937" : "#1f2937",
    padding: 10,
    cursor: state.isDisabled ? "not-allowed" : "pointer",
  }),
  control: (provided, state) => ({
    ...provided,
    borderColor: state.isFocused ? "#325BED" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 1px #325BED" : "none",
    cursor: state.isDisabled ? "not-allowed" : "default",
    backgroundColor: state.isDisabled ? "#f3f4f6" : "#fff",
    marginTop: 0,
    borderRadius: 7,
  }),
  singleValue: (provided, state) => ({
    ...provided,
    color: state.isDisabled ? "#9ca3af" : "#1f2937",
    fontSize: "0.9rem",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menu: (base) => ({
    ...base,
    minWidth: 0,
    overflowX: "hidden",
  }),
  menuList: (base) => ({
    ...base,
    minWidth: 0,
    overflowX: "hidden",
  }),
};

const CustomSelect = ({
  label,
  placeholder,
  options,
  selected,
  setSelected,
  tooltip,
  className,
  isClearable = true,
  maxMenuHeight = 300,
  disabled = false,
  showDescriptionBelowLabel = false,
}: SelectProps) => {
  const handleChange = (option: any) => {
    setSelected(option);
  };

  return (
    <div className="flex flex-col w-full">
      <label
        htmlFor={label.toLowerCase().replace(" ", "-")}
        className="mb-1 flex items-center gap-2 uppercase text-xs tracking-wide leading-6"
      >
        {label}{" "}
        {tooltip && (
          <InfoTooltip
            id={label}
            tooltip={tooltip}
          />
        )}
      </label>
      <div className={className}>
        <Select
          id={label.toLowerCase().replace(" ", "-")}
          placeholder={placeholder}
          value={selected}
          onChange={handleChange}
          options={options}
          components={{
            Option: CustomOption(showDescriptionBelowLabel),
            SingleValue: SingleValueComponent,
          }}
          isSearchable={false}
          menuPortalTarget={document.body}
          styles={customStyles}
          isClearable={isClearable}
          maxMenuHeight={maxMenuHeight}
          isDisabled={disabled}
        />
      </div>
    </div>
  );
};

export default CustomSelect;
